import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {ProductService} from "../service/product.service";
import {ProductResponse} from "../models/productResponse";
import {Subscription} from 'rxjs';
import {LanguageService} from "../service/language.service";
import {ShoppingCartService} from "../service/shoppingCart.service";
import {UserService} from "../service/user.service";
import {ShoppingCartResponse} from "../models/shoppingCartResponse";

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss', './products-media.component.scss']
})
export class ProductsComponent implements OnInit, OnDestroy {
  pageSize: number = 12;
  currentPage: number = 0;
  totalPageCount: number | any;
  isLoading: boolean = false;
  products: ProductResponse[] = [];
  currentLanguage: string = '';
  private languageSubscription: Subscription | undefined;
  private cartProductIds: Set<number> = new Set<number>();
  private cartLoaded: boolean = false;

  constructor(
    private productService: ProductService,
    private languageService: LanguageService,
    private shoppingCartService: ShoppingCartService,
    private userService: UserService
  ) {
  }

  ngOnInit() {
    this.languageSubscription = this.languageService.currentLanguage$.subscribe((lang) => {
      this.currentLanguage = lang;
      this.currentPage = 0;
      this.products = [];
      this.cartProductIds.clear();
      this.cartLoaded = false;
      this.loadCartProductIds();
      this.loadProducts();
    });
  }

  loadProducts() {
    if (this.isLoading || (this.totalPageCount && this.currentPage >= this.totalPageCount)) {
      return;
    }
    this.isLoading = true;

    this.productService.getActiveProducts(this.currentPage, this.pageSize, this.currentLanguage, true).subscribe({
      next: (response) => {
        if (response && response.content) {
          this.applyCartState(response.content);
          this.products = [...this.products, ...response.content];
          this.totalPageCount = response.totalPages;
          this.currentPage++;
        }
      },
      error: () => {
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  private loadCartProductIds() {
    if (!this.userService.isLoggedIn()) {
      this.cartLoaded = true;
      this.applyCartState(this.products);
      return;
    }

    this.shoppingCartService.getBucketForCurrentUser(this.currentLanguage).subscribe({
      next: (response: ShoppingCartResponse) => {
        const ids = (response.items || [])
          .map((item) => item.product?.id)
          .filter((id): id is number => typeof id === 'number');
        this.cartProductIds = new Set<number>(ids);
        this.cartLoaded = true;
        this.applyCartState(this.products);
      },
      error: () => {
        this.cartLoaded = true;
      }
    });
  }

  private applyCartState(products: ProductResponse[]) {
    if (!this.cartLoaded || !products?.length) {
      return;
    }

    products.forEach((product) => {
      product.isAddedToCart = this.cartProductIds.has(product.id);
    });
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200 && !this.isLoading && this.currentPage < this.totalPageCount
    ) {
      this.loadProducts();
    }
  }

  ngOnDestroy() {
    this.languageSubscription?.unsubscribe();
  }
}
